/*
Copyright 2021-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  useCallback,
  useEffect,
  useState,
  createContext,
  use,
  useRef,
  useMemo,
  type JSX,
} from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "matrix-js-sdk/lib/logger";
import { type ISyncStateData, type SyncState } from "matrix-js-sdk/lib/sync";
import { ClientEvent, type MatrixClient } from "matrix-js-sdk";

import type { WidgetApi } from "matrix-widget-api";
import { ErrorPage } from "./FullScreenView";
import { useTranslation } from "react-i18next";
import { RoomAccessNotice } from "./room/RoomAccessNotice";
import { SessionHomeserverMismatch } from "./auth/sessionHomeserver";
import {
  archiveForeignSession,
  discardStaffSession,
  pendingGuestSessionKey,
} from "./auth/sessionRecovery";
import { widget } from "./widget";
import {
  PosthogAnalytics,
  RegistrationType,
} from "./analytics/PosthogAnalytics";
import { useEventTarget } from "./useEvents";
import { OpenElsewhereError } from "./RichError";
import { persistentStaffSession } from "./auth/staffSession";
import { isGuestOrigin, isAdminOrigin } from "./auth/access";
import { joinAccountCommand, syncJoinAccount } from "./auth/accountSync";
import { backendFetch, BackendError } from "./utils/backend";
import { revokeJoinSession } from "./auth/joinSession";
import { initRecordingClient } from "./auth/recordingClient";

declare global {
  interface Window {
    matrixclient: MatrixClient;
    passwordlessUser: boolean;
  }
}

export type ClientState = ValidClientState | ErrorState;

export type ValidClientState = {
  state: "valid";
  authenticated?: AuthenticatedClient;
  // 'Disconnected' rather than 'connected' because it tracks specifically
  // whether the client is supposed to be connected but is not
  disconnected: boolean;
  supportedFeatures: {
    reactions: boolean;
  };
  setClient: (client: MatrixClient, session: Session) => void;
  resetForStaffLogin?: () => Promise<void>;
};

export type AuthenticatedClient = {
  client: MatrixClient;
  isPasswordlessUser: boolean;
  changePassword: (password: string) => Promise<void>;
  logout: () => void;
  confirmStaffSession?: (userId: string) => void;
};

export type ErrorState = {
  state: "error";
  error: Error;
};

const ClientContext = createContext<ClientState | undefined>(undefined);

export const ClientContextProvider = ClientContext.Provider;

export const useClientState = (): ClientState | undefined => use(ClientContext);

export function useClient(): {
  client?: MatrixClient;
  setClient?: (client: MatrixClient, session: Session) => void;
} {
  let client;
  let setClient;

  const clientState = useClientState();
  if (clientState?.state === "valid") {
    client = clientState.authenticated?.client;
    setClient = clientState.setClient;
  }

  return { client, setClient };
}

// Plain representation of the `ClientContext` as a helper for old components that expected an object with multiple fields.
export function useClientLegacy(): {
  client?: MatrixClient;
  setClient?: (client: MatrixClient, session: Session) => void;
  passwordlessUser: boolean;
  loading: boolean;
  authenticated: boolean;
  logout?: () => void;
  confirmStaffSession?: (userId: string) => void;
  resetForStaffLogin?: () => Promise<void>;
  error?: Error;
} {
  const clientState = useClientState();

  let client;
  let setClient;
  let passwordlessUser = false;
  let loading = true;
  let error;
  let authenticated = false;
  let logout;
  let confirmStaffSession;
  let resetForStaffLogin;

  if (clientState?.state === "valid") {
    client = clientState.authenticated?.client;
    setClient = clientState.setClient;
    passwordlessUser = clientState.authenticated?.isPasswordlessUser ?? false;
    loading = false;
    authenticated = client !== undefined;
    logout = clientState.authenticated?.logout;
    confirmStaffSession = clientState.authenticated?.confirmStaffSession;
    resetForStaffLogin = clientState.resetForStaffLogin;
  } else if (clientState?.state === "error") {
    error = clientState.error;
    loading = false;
  }

  return {
    client,
    setClient,
    passwordlessUser,
    loading,
    authenticated,
    logout,
    confirmStaffSession,
    resetForStaffLogin,
    error,
  };
}

const loadChannel =
  "BroadcastChannel" in window ? new BroadcastChannel("load") : null;

interface Props {
  recordingOnly?: boolean;
  children: JSX.Element;
}

export const ClientProvider: FC<Props> = ({
  children,
  recordingOnly = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initializationError, setInitializationError] = useState<Error>();

  // null = signed out, undefined = loading
  const [initClientState, setInitClientState] = useState<
    InitResult | null | undefined
  >(undefined);

  const initializing = useRef(false);
  useEffect(() => {
    // In case the component is mounted, unmounted, and remounted quickly (as
    // React does in strict mode), we need to make sure not to doubly initialize
    // the client.
    if (initializing.current) return;
    initializing.current = true;

    loadClient(recordingOnly)
      .then((initResult) => {
        setInitClientState(initResult);
        if (PosthogAnalytics.instance.isEnabled())
          PosthogAnalytics.instance.startListeningToSettingsChanges();
      })
      .catch((err) => {
        if (err instanceof SessionHomeserverMismatch && !widget &&
            !recordingOnly && isAdminOrigin(window.location.origin) &&
            !isGuestOrigin(window.location.origin)) {
          // Old cabinet builds could save a guest from another project.
          // No foreign client was initialized; discard only its auth markers.
          discardStaffSession(localStorage);
          setInitClientState(null);
          return;
        }
        logger.error("Matrix client initialization failed", err);
        setInitializationError(
          err instanceof Error ? err : new Error(String(err)),
        );
      })
      .finally(() => (initializing.current = false));
  }, [recordingOnly]);

  const changePassword = useCallback(
    async (password: string) => {
      const session = loadSession();
      if (!initClientState?.client || !session) {
        return;
      }

      await initClientState.client.setPassword(
        {
          type: "m.login.password",
          identifier: {
            type: "m.id.user",
            user: session.user_id,
          },
          user: session.user_id,
          password: session.tempPassword,
        },
        password,
      );

      saveSession({ ...session, passwordlessUser: false });

      setInitClientState({
        ...initClientState,
        passwordlessUser: false,
      });
    },
    [initClientState],
  );

  const confirmStaffSession = useCallback(
    (userId: string) => {
      const client = initClientState?.client;
      const session = loadSession();
      if (!client || client.getUserId() !== userId || !session) return;
      const updated = persistentStaffSession(
        session,
        userId,
        client.getAccessToken() ?? null,
      );
      if (!updated || !session.passwordlessUser) return;
      saveSession(updated);
      setInitClientState((state) =>
        state && state.client === client
          ? { ...state, passwordlessUser: false }
          : state,
      );
    },
    [initClientState?.client],
  );

  const setClient = useCallback(
    (client: MatrixClient, session: Session) => {
      const oldClient = initClientState?.client;
      if (oldClient && oldClient !== client) {
        oldClient.stopClient();
      }

      saveSession(session);
      setInitClientState({
        widgetApi: null,
        client,
        passwordlessUser: session.passwordlessUser,
      });
      if (PosthogAnalytics.instance.isEnabled())
        PosthogAnalytics.instance.startListeningToSettingsChanges();
    },
    [initClientState?.client],
  );

  const resetForStaffLogin = useCallback(async (): Promise<void> => {
    discardStaffSession(localStorage, initClientState?.client);
    setInitClientState(null);
    // A successful new sign-in clears old SDK stores before initializing crypto.
    return Promise.resolve();
  }, [initClientState?.client]);

  const logout = useCallback(async () => {
    const client = initClientState?.client;
    if (!client) {
      return;
    }

    if (!isGuestOrigin(window.location.origin))
      await revokeJoinSession(client).catch(() => {});
    await client.logout(true);
    await client.clearStores();
    clearSession();
    setInitClientState(null);
    if (!isGuestOrigin(window.location.origin) && !client.isGuest() && !initClientState?.passwordlessUser && import.meta.env.VITE_GUEST_URL) {
      const signal=AbortSignal.timeout(9000);
      await joinAccountCommand("clear",client.getUserId()!,signal).catch(()=>{});
    }

    await navigate("/");
    PosthogAnalytics.instance.logout();
    PosthogAnalytics.instance.setRegistrationType(RegistrationType.Guest);
  }, [navigate, initClientState?.client, initClientState?.passwordlessUser]);

  useEffect(() => {
    const client=initClientState?.client;
    if(widget || !client || initClientState.passwordlessUser || !import.meta.env.VITE_GUEST_URL || !import.meta.env.VITE_ADMIN_URL)return;
    const controller=new AbortController();
    if(!isGuestOrigin(window.location.origin)) {
      void syncJoinAccount(client,controller.signal).catch(()=>{});
      return ()=>controller.abort();
    }
    // Catch revoked join devices even when their page stayed open in another
    // tab, or when immediate cleanup was interrupted/offline during logout.
    let checking=false;
    const check=async (): Promise<void> => {
      if(checking || controller.signal.aborted)return;
      checking=true;
      try { await backendFetch(client,"/api/auth/join-session/status",{method:"POST",signal:controller.signal}); }
      catch(error) {
        if(!controller.signal.aborted && error instanceof BackendError && [401,403].includes(error.status)) {
          const stored=loadSession();
          if(stored?.access_token===client.getAccessToken()) {
            client.stopClient();clearSession();setInitClientState(null);
          }
        }
      } finally { checking=false; }
    };
    const onVisible=(): void=>{if(document.visibilityState==="visible")void check();};
    const timer=window.setInterval(()=>void check(),30000);
    window.addEventListener("focus",onVisible);document.addEventListener("visibilitychange",onVisible);
    void check();
    return ()=>{controller.abort();clearInterval(timer);window.removeEventListener("focus",onVisible);document.removeEventListener("visibilitychange",onVisible);};
  },[initClientState]);

  useEffect(()=>{
    if(widget)return;
    const changed=(event:StorageEvent):void=>{
      if(event.key!=="matrix-auth-store" || event.storageArea!==localStorage)return;
      let previous: Session | null, next: Session | null;
      try {
        previous=event.oldValue ? JSON.parse(event.oldValue) as Session : null;
        next=event.newValue ? JSON.parse(event.newValue) as Session : null;
      } catch { return; }
      if(previous?.access_token===next?.access_token)return;
      initClientState?.client.stopClient();
      window.location.reload();
    };
    window.addEventListener("storage",changed);
    return ()=>window.removeEventListener("storage",changed);
  },[initClientState]);

  // To protect against multiple sessions writing to the same storage
  // simultaneously, we send a broadcast message that shuts down all other
  // running instances of the app. This isn't necessary if the app is running in
  // a widget though, since then it'll be mostly stateless.
  useEffect(() => {
    if (!widget && !recordingOnly) loadChannel?.postMessage({});
  }, [recordingOnly]);

  const [alreadyOpenedErr, setAlreadyOpenedErr] = useState<Error | undefined>(
    undefined,
  );
  useEventTarget(
    loadChannel,
    "message",
    useCallback(() => {
      if (recordingOnly) return;
      initClientState?.client.stopClient();
      setAlreadyOpenedErr(new OpenElsewhereError());
    }, [initClientState?.client, setAlreadyOpenedErr, recordingOnly]),
  );

  const [isDisconnected, setIsDisconnected] = useState(false);
  const [supportsReactions, setSupportsReactions] = useState(false);

  const state: ClientState | undefined = useMemo(() => {
    if (alreadyOpenedErr) {
      return { state: "error", error: alreadyOpenedErr };
    }

    if (initializationError)
      return { state: "error", error: initializationError };
    if (initClientState === undefined) return undefined;

    const authenticated =
      initClientState === null
        ? undefined
        : {
            client: initClientState.client,
            isPasswordlessUser: initClientState.passwordlessUser,
            changePassword,
            logout,
            confirmStaffSession,
          };

    return {
      state: "valid",
      authenticated,
      setClient,
      resetForStaffLogin,
      disconnected: isDisconnected,
      supportedFeatures: {
        reactions: supportsReactions,
      },
    };
  }, [
    alreadyOpenedErr,
    initializationError,
    changePassword,
    confirmStaffSession,
    initClientState,
    logout,
    setClient,
    resetForStaffLogin,
    isDisconnected,
    supportsReactions,
  ]);

  const onSync = useCallback(
    (state: SyncState, _old: SyncState | null, data?: ISyncStateData) => {
      setIsDisconnected(clientIsDisconnected(state, data));
    },
    [],
  );

  useEffect(() => {
    if (!initClientState) {
      return;
    }

    window.matrixclient = initClientState.client;
    window.passwordlessUser = initClientState.passwordlessUser;

    if (PosthogAnalytics.hasInstance())
      PosthogAnalytics.instance.onLoginStatusChanged();

    if (initClientState.client) {
      initClientState.client.on(ClientEvent.Sync, onSync);
    }

    if (initClientState.widgetApi) {
      const reactSend = initClientState.widgetApi.hasCapability(
        "org.matrix.msc2762.send.event:m.reaction",
      );
      const redactSend = initClientState.widgetApi.hasCapability(
        "org.matrix.msc2762.send.event:m.room.redaction",
      );
      const reactRcv = initClientState.widgetApi.hasCapability(
        "org.matrix.msc2762.receive.event:m.reaction",
      );
      const redactRcv = initClientState.widgetApi.hasCapability(
        "org.matrix.msc2762.receive.event:m.room.redaction",
      );

      if (!reactSend || !reactRcv || !redactSend || !redactRcv) {
        logger.warn("Widget does not support reactions");
        setSupportsReactions(false);
      } else {
        setSupportsReactions(true);
      }
    } else {
      setSupportsReactions(true);
    }

    return (): void => {
      if (initClientState.client) {
        initClientState.client.removeListener(ClientEvent.Sync, onSync);
      }
    };
  }, [initClientState, onSync]);

  if (alreadyOpenedErr) {
    return <ErrorPage widget={widget} error={alreadyOpenedErr} />;
  }

  if (initializationError) {
    return (
      <RoomAccessNotice
        title={t("instance.ui.session_restore_failed")}
        description={
          initializationError instanceof SessionHomeserverMismatch
            ? t("instance.ui.foreign_session_preserved")
            : t("instance.ui.session_restore_retry")
        }
        action={
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {!(
              initializationError instanceof SessionHomeserverMismatch &&
              isGuestOrigin(window.location.origin) &&
              !recordingOnly
            ) && (
              <button
                className="glass-button"
                onClick={() => window.location.reload()}
              >
                {t("instance.ui.retry_sign_in")}
              </button>
            )}
            {initializationError instanceof SessionHomeserverMismatch &&
              isGuestOrigin(window.location.origin) &&
              !recordingOnly && (
                <button
                  className="glass-button"
                  onClick={() => {
                    try {
                      archiveForeignSession(localStorage);
                      setInitClientState(null);
                      setInitializationError(undefined);
                    } catch {
                      /* Preserve the existing session if the archive cannot be saved. */
                    }
                  }}
                >
                  {t("instance.ui.continue_as_guest")}
                </button>
              )}
          </div>
        }
      />
    );
  }
  return <ClientContext value={state}>{children}</ClientContext>;
};

export type InitResult = {
  widgetApi: WidgetApi | null;
  client: MatrixClient;
  passwordlessUser: boolean;
};

async function loadClient(recordingOnly = false): Promise<InitResult | null> {
  if (recordingOnly && !widget) {
    return initRecordingClient(loadSession, window.location.search);
  }
  if (widget) {
    // We're inside a widget, so let's engage *matryoshka mode*
    logger.log("Using a matryoshka client");
    const client = await widget.client;
    return {
      widgetApi: widget.api,
      client,
      passwordlessUser: false,
    };
  } else {
    const { initSPA } = await import("./utils/spa");
    return initSPA(loadSession, clearSession, saveSession);
  }
}

export interface Session {
  user_id: string;
  device_id: string;
  access_token: string;
  passwordlessUser: boolean;
  tempPassword?: string;
  homeserver_url?: string;
  reset_crypto?: boolean;
  storage_namespace?: string;
  pending_display_name?: string;
}

const clearSession = (): void => {
  localStorage.removeItem("matrix-auth-store");
  localStorage.removeItem(pendingGuestSessionKey);
};
const saveSession = (s: Session): void => {
  localStorage.setItem("matrix-auth-store", JSON.stringify(s));
  localStorage.removeItem(pendingGuestSessionKey);
};
const loadSession = (): Session | undefined => {
  const data =
    localStorage.getItem("matrix-auth-store") ??
    localStorage.getItem(pendingGuestSessionKey);
  if (!data) {
    return undefined;
  }

  return JSON.parse(data);
};

const clientIsDisconnected = (
  syncState: SyncState,
  syncData?: ISyncStateData,
): boolean =>
  syncState === "ERROR" && syncData?.error?.name === "ConnectionError";
